import { supabase } from './supabaseClient';
import type { Worker, ShiftSchedule, WorkerTraining, DisciplinaryRecord, RecruitmentCandidate } from '../types/worker';

const TABLE = 'workers';
const SHIFTS_TABLE = 'shift_schedules';
const TRAINING_TABLE = 'worker_training';
const DISCIPLINARY_TABLE = 'disciplinary_records';
const RECRUITMENT_TABLE = 'recruitment_candidates';

// Helper: camelCase to snake_case
const toSnake = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toSnake);
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [
      k.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`),
      typeof v === 'object' && v !== null ? toSnake(v) : v
    ])
  );
};

// Helper: snake_case to camelCase
const toCamel = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toCamel);
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [
      k.replace(/_([a-z])/g, (_, l) => l.toUpperCase()),
      typeof v === 'object' && v !== null ? toCamel(v) : v
    ])
  );
};

export const fetchWorkers = async (farmId?: string): Promise<Worker[]> => {
  let query = supabase.from(TABLE).select('*');
  if (farmId) query = query.eq('farm_id', farmId);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(toCamel);
};

export const addWorker = async (worker: Omit<Worker, 'id'> & { farmId?: string }): Promise<Worker> => {
  const toInsert = toSnake(worker);
  if (worker.farmId) toInsert.farm_id = worker.farmId;
  const { data, error } = await supabase.from(TABLE).insert([toInsert]).select().single();
  if (error) throw error;
  return toCamel(data);
};

export const updateWorker = async (id: string, updates: Partial<Worker>): Promise<Worker> => {
  const { data, error } = await supabase.from(TABLE).update(toSnake(updates)).eq('id', id).select().single();
  if (error) throw error;
  return toCamel(data);
};

export const deleteWorker = async (id: string): Promise<void> => {
  const { error } = await supabase.from(TABLE).delete().eq('id', id);
  if (error) throw error;
};

// Shifts
export const fetchShifts = async (farmId: string): Promise<ShiftSchedule[]> => {
  const { data, error } = await supabase.from(SHIFTS_TABLE).select('*').eq('farm_id', farmId);
  if (error) throw error;
  return (data || []).map(toCamel);
};

export const addShift = async (shift: Partial<ShiftSchedule>): Promise<ShiftSchedule> => {
  const { data, error } = await supabase.from(SHIFTS_TABLE).insert([toSnake(shift)]).select().single();
  if (error) throw error;
  return toCamel(data);
};

// Training
export const fetchTraining = async (farmId: string): Promise<WorkerTraining[]> => {
  const { data, error } = await supabase.from(TRAINING_TABLE).select('*').eq('farm_id', farmId);
  if (error) throw error;
  return (data || []).map(toCamel);
};

export const addTraining = async (training: Partial<WorkerTraining>): Promise<WorkerTraining> => {
  const { data, error } = await supabase.from(TRAINING_TABLE).insert([toSnake(training)]).select().single();
  if (error) throw error;
  return toCamel(data);
};

// Disciplinary
export const fetchDisciplinary = async (farmId: string): Promise<DisciplinaryRecord[]> => {
  const { data, error } = await supabase.from(DISCIPLINARY_TABLE).select('*').eq('farm_id', farmId);
  if (error) throw error;
  return (data || []).map(toCamel);
};

export const addDisciplinary = async (record: Partial<DisciplinaryRecord>): Promise<DisciplinaryRecord> => {
  const { data, error } = await supabase.from(DISCIPLINARY_TABLE).insert([toSnake(record)]).select().single();
  if (error) throw error;
  return toCamel(data);
};

// Recruitment
export const fetchCandidates = async (farmId: string): Promise<RecruitmentCandidate[]> => {
  const { data, error } = await supabase.from(RECRUITMENT_TABLE).select('*').eq('farm_id', farmId);
  if (error) throw error;
  return (data || []).map(toCamel);
};

export const addCandidate = async (candidate: Partial<RecruitmentCandidate>): Promise<RecruitmentCandidate> => {
  const { data, error } = await supabase.from(RECRUITMENT_TABLE).insert([toSnake(candidate)]).select().single();
  if (error) throw error;
  return toCamel(data);
};

export const updateCandidateStage = async (id: string, stage: string): Promise<void> => {
  const { error } = await supabase.from(RECRUITMENT_TABLE).update({ stage }).eq('id', id);
  if (error) throw error;
};

// Attendance
export const fetchAttendance = async (farmId: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from('worker_attendance')
    .select(`*, workers!inner(name, farm_id)`)
    .eq('workers.farm_id', farmId)
    .order('date', { ascending: false });
  if (error) throw error;
  return (data || []).map(record => ({
    ...toCamel(record),
    workerName: record.workers?.name || 'Unknown'
  }));
};

// Performance
export const fetchPerformanceReviews = async (farmId: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from('worker_performance_reviews')
    .select(`*, workers!inner(name, farm_id)`)
    .eq('workers.farm_id', farmId)
    .order('date', { ascending: false });
  if (error) throw error;
  return (data || []).map(review => ({
    ...toCamel(review),
    workerName: review.workers?.name || 'Unknown'
  }));
};

// Payroll
export const fetchPayrollRecords = async (farmId: string): Promise<any[]> => {
  const { data, error } = await supabase
    .from('payroll_records')
    .select('*')
    .eq('farm_id', farmId)
    .order('period_start', { ascending: false });
  if (error) throw error;
  return (data || []).map(toCamel);
};
